import type { Event } from '@sentry/react';

// Production is served on app.curium.app and the apex curium.app (CUR-3056); staging is
// curiumstaging.app (not a curium.app subdomain).
const CURIUM_TRPC_URL_PATTERN = /^https:\/\/(?:(?:app\.)?curium\.app|curiumstaging\.app)\/trpc(?:\/|$)/;
const TRANSIENT_EDGE_HTTP_STATUS_CODES = [502, 503, 504, 520, 521, 522, 523, 524] as const;

function isCloudflareBeaconScriptLoadError(event: Event): boolean {
    return (
        event.exception?.values?.[0]?.type === 'ScriptLoadError' &&
        event.tags?.script?.toString().includes('cloudflareinsights.com') === true
    );
}

function getExceptionMechanismType(event: Event): string | undefined {
    return event.exception?.values?.[0]?.mechanism?.type;
}

function getResponseStatusCode(event: Event): number | undefined {
    const response = event.contexts?.response;
    if (!response || typeof response !== 'object') return undefined;

    const statusCode = (response as { status_code?: unknown }).status_code;
    if (typeof statusCode === 'number') return statusCode;
    if (typeof statusCode !== 'string') return undefined;

    const parsed = Number(statusCode);
    return Number.isInteger(parsed) ? parsed : undefined;
}

function isTransientEdgeStatusCode(statusCode: number | undefined): boolean {
    return TRANSIENT_EDGE_HTTP_STATUS_CODES.includes(statusCode as (typeof TRANSIENT_EDGE_HTTP_STATUS_CODES)[number]);
}

function isCuriumTrpcUrl(url: string | undefined): boolean {
    return url !== undefined && CURIUM_TRPC_URL_PATTERN.test(url);
}

function isHttpClientMechanism(mechanismType: string | undefined): boolean {
    // httpClientIntegration tags the mechanism as `auto.http.client.${transport}`
    // (e.g. `auto.http.client.fetch`, `auto.http.client.xhr`).
    return mechanismType?.startsWith('auto.http.client.') === true;
}

function isTransientCuriumTrpcHttpClientError(event: Event): boolean {
    return (
        isHttpClientMechanism(getExceptionMechanismType(event)) &&
        isCuriumTrpcUrl(event.request?.url) &&
        isTransientEdgeStatusCode(getResponseStatusCode(event))
    );
}

export function filterSentryEvent<TEvent extends Event>(event: TEvent): TEvent | null {
    if (isCloudflareBeaconScriptLoadError(event)) {
        return null;
    }

    if (isTransientCuriumTrpcHttpClientError(event)) {
        return null;
    }

    return event;
}
